import { ApiError } from './api/client.js';
import { UserFacingError } from './user-facing-error.js';

export interface ErrorView {
  /** Plain-English, for the person. Never a stack, a status line or a browser's own wording. */
  message: string;
  /** The API's request id, for support to trace. Only on errors that came from the API. */
  reference?: string;
  /** Whether trying again could plausibly help — decides whether "Try again" is offered. */
  retryable: boolean;
}

const OFFLINE = "We couldn't reach VoxFaunix. Check your connection and try again.";
const GENERIC = 'Something went wrong. Please try again.';

/** The API's own default when its envelope carried no message — never worth showing. */
const isBareStatus = (message: string) => /^Request failed \(\d+\)$/.test(message);

/**
 * Turns anything that can be thrown into something a contributor should see.
 *
 * Pure and total: it never throws and never returns a raw `error.message`
 * unless that message was written for people (a `UserFacingError`, or the
 * API's own envelope on a 4xx, which is the server telling the client what it
 * got wrong). That rule is the whole point — `Failed to fetch` and
 * `Request failed (500)` are for developers, and a professional product does
 * not put them in front of the person it's asking to contribute.
 */
export function describeError(error: unknown): ErrorView {
  if (error instanceof ApiError) {
    const reference = error.requestId;
    const { status } = error;

    if (status === 401) {
      return { message: 'Your session has expired. Please sign in again.', retryable: false };
    }
    if (status === 403) {
      return { message: "You don't have permission to do that.", retryable: false };
    }
    if (status === 404) {
      return {
        message: "We couldn't find that. It may have been removed.",
        ...(reference ? { reference } : {}),
        retryable: false,
      };
    }
    if (status === 408 || status === 429) {
      return {
        message: 'Too many requests just now. Wait a moment, then try again.',
        ...(reference ? { reference } : {}),
        retryable: true,
      };
    }
    if (status >= 500) {
      return {
        message: 'Something went wrong on our side, not yours. Try again in a moment.',
        ...(reference ? { reference } : {}),
        retryable: true,
      };
    }

    // 400 / 409 / 422 and friends: the server explained what it rejected. Trust
    // that, unless the envelope was empty and all we have is the status line.
    return {
      message: isBareStatus(error.message)
        ? "We couldn't complete that. Please check what you entered and try again."
        : error.message,
      ...(reference ? { reference } : {}),
      retryable: false,
    };
  }

  if (error instanceof UserFacingError) {
    return { message: error.message, retryable: true };
  }

  // fetch rejects with a TypeError for offline, DNS, CORS and blocked requests
  // ("Failed to fetch" / "Load failed" / "NetworkError…" depending on browser);
  // an abort is our own timeout. None of them are worth showing as written.
  const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
  if (
    offline ||
    error instanceof TypeError ||
    (error instanceof DOMException && error.name === 'AbortError')
  ) {
    return { message: OFFLINE, retryable: true };
  }

  return { message: GENERIC, retryable: true };
}
